import React, { useEffect, useMemo, useState } from "react";
import { CheckCircle2, ChevronDown, ChevronUp, CircleHelp, RotateCcw } from "lucide-react";
import {
  collectRefinementCandidates,
  exactOfficialNameCandidates,
  condenseHeadingLabel,
  describeProposalBasis,
  formatHsk,
  headingQualifier,
  needsMoreSpecificName,
  proposeFromSearchEvidence,
} from "./searchRefinement";
import { GoogleHsCrosscheck } from "./GoogleSearchActions";
import OfficialFrameEvidenceForm, {
  admissibleSourcesOf,
  buildOfficialEvidence,
  emptyOfficialEvidence,
  isOfficialEvidenceReady,
  optionRequiresEvidence,
  questionRequiresEvidence,
} from "./OfficialFrameEvidenceForm";

const CACHE_TTL = 25 * 60 * 1000;

/**
 * Bump when the response shape or the server's traversal behaviour changes.
 *
 * <p>A cached flow is a rendered server state, not just data. After a deploy the old one keeps
 * showing until the user clicks — which is the most likely explanation for a screenshot of the 4202
 * search sitting inside the 4205 frame, since replaying that request now lands on 4202 correctly.
 * The session dies with the restart so the next interaction self-heals, but the stale screen in
 * front of the user does not, and it is the screen they judge the product by.
 */
const CACHE_SCHEMA = "v2-proposed-path";

function storageKey(query, candidateKey) {
  return `loginhs:search-classification:${CACHE_SCHEMA}:${String(query || "").trim()}:${candidateKey}`;
}

function readCached(query, candidateKey) {
  if (!query || !candidateKey) return null;
  try {
    const value = JSON.parse(sessionStorage.getItem(storageKey(query, candidateKey)) || "null");
    if (!value || !value.sessionId || !value.response || Date.now() - value.savedAt > CACHE_TTL) return null;
    return value;
  } catch {
    return null;
  }
}

function saveCached(query, candidateKey, value) {
  try {
    sessionStorage.setItem(storageKey(query, candidateKey), JSON.stringify({ ...value, savedAt: Date.now() }));
  } catch { /* private browsing */ }
}

function stageLabel(question, response) {
  if (response && response.official_frame_v2) {
    const code = question && question.official_code;
    return `공식 다단계 분류${code ? ` · ${formatHsk(code)}` : ""}`;
  }
  const key = question && question.property_key;
  if (key === "__section__") return "1단계 · 부(상품군) 선택";
  if (key === "__chapter__") return "2단계 · 류 선택";
  if (key === "__heading__") return "3단계 · 호 선택";
  return "후보 확인";
}

function optionFacts(option) {
  const required = Array.isArray(option.required_facts) ? option.required_facts : [];
  const excluded = Array.isArray(option.exclusion_facts) ? option.exclusion_facts : [];
  return { required, excluded };
}

export default function SearchRefinementPanel({ q, data, onRowClick, defaultOpen = true }) {
  const candidates = useMemo(() => collectRefinementCandidates(data, 24, q), [data, q]);
  const candidateKey = useMemo(() => candidates.map((candidate) => candidate.hsk.slice(0, 4)).join(","), [candidates]);
  // 검색이 이미 가져온 사례로 공식 경로를 미리 채워 볼 세번. 제안일 뿐이며 사용자가 확정해야
  // 적용된다 — 근거는 화면에 그대로 적는다.
  const proposal = useMemo(
    () => proposeFromSearchEvidence(
      data && data.cases,
      data && data.rulings,
      exactOfficialNameCandidates(q, data && data.tariff && data.tariff.items),
    ),
    [data, q],
  );
  const [flow, setFlow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // 결정트리 사전 점검 카드가 프로세스를 이끌 때는 접어 두고, 카드 안 '직접 고르기'나 이 헤더로 연다.
  const [open, setOpen] = useState(defaultOpen);
  const [reloadToken, setReloadToken] = useState(0);
  const [officialEvidence, setOfficialEvidence] = useState(emptyOfficialEvidence);
  const [selectedOfficialOption, setSelectedOfficialOption] = useState(null);

  useEffect(() => {
    if (!q || !candidateKey || !candidates.length) {
      setFlow(null);
      return undefined;
    }
    const cached = readCached(q, candidateKey);
    if (cached) {
      setFlow(cached);
      setError("");
      setOpen(true);
      return undefined;
    }

    const controller = new AbortController();
    setLoading(true);
    setError("");
    fetch("/api/onto/action/ClassifySearchCandidates", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      signal: controller.signal,
      body: JSON.stringify({
        keyword: q,
        candidates,
        ...(proposal ? { proposed_hsk: proposal.hsk } : {}),
      }),
    })
      .then((response) => response.ok ? response.json() : Promise.reject(new Error(`HTTP ${response.status}`)))
      .then((response) => {
        const next = { sessionId: response.session_id, response };
        setFlow(next);
        saveCached(q, candidateKey, next);
      })
      .catch((requestError) => {
        if (requestError.name !== "AbortError") {
          setFlow(null);
          setError("공식 분류 흐름을 시작하지 못했습니다. 잠시 후 다시 시도해 주세요.");
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
    // candidateKey is the stable identity of candidates; the array itself may be recreated by search rendering.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, candidateKey, reloadToken, proposal]);

  const response = flow && flow.response;
  const question = response && response.next_question;
  const questionNodeId = question?.node_id || question?.fact_id;
  const options = (question && Array.isArray(question.options)) ? question.options : [];
  const terminalHsk = response && response.is_terminal ? response.terminal_hsk : null;
  // 근거 폼은 경계 게이트(boundary-gate-v1)에만 남는다. 공식 프레임 분기는 선택 즉시 제출하고
  // 서버가 selection_only 로 기록한다 — 분기마다 출처·요약·체크박스를 받던 것이 5클릭의 실체였다.
  const isOfficialQuestion = questionRequiresEvidence(question)
    && question?.source !== "official-frame-v2";
  const evidenceReady = !isOfficialQuestion
    || isOfficialEvidenceReady(officialEvidence, selectedOfficialOption);

  useEffect(() => {
    setOfficialEvidence(emptyOfficialEvidence());
    setSelectedOfficialOption(null);
  }, [questionNodeId]);

  // 후보가 너무 여러 부에 걸쳐 있으면 「부를 고르라」는 질문 자체가 답할 수 없는 질문이다.
  // 「말」은 한 글자라 토크나이저가 버리고 남는 것이 임베딩의 느슨한 결과뿐이다.
  const tooBroad = needsMoreSpecificName(data, q);

  if (!candidates.length || (!flow && !loading && !error)) return null;

  const answer = async (option) => {
    if (!flow || !question || loading) return;
    if (isOfficialQuestion && !isOfficialEvidenceReady(officialEvidence, option)) return;
    setLoading(true);
    setError("");
    try {
      const apiResponse = await fetch("/api/onto/action/AnswerQuestion", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: flow.sessionId,
          property_key: question.property_key,
          option_value: option.value,
          ...(isOfficialQuestion
            ? { evidence: buildOfficialEvidence(officialEvidence, option) }
            : {}),
        }),
      });
      if (!apiResponse.ok) {
        if (apiResponse.status === 404) {
          sessionStorage.removeItem(storageKey(q, candidateKey));
          setReloadToken((value) => value + 1);
          return;
        }
        throw new Error(`HTTP ${apiResponse.status}`);
      }
      const nextResponse = await apiResponse.json();
      const next = { sessionId: flow.sessionId, response: nextResponse };
      setFlow(next);
      saveCached(q, candidateKey, next);
    } catch {
      setError("선택을 반영하지 못했습니다. 다시 선택해 주세요.");
    } finally {
      setLoading(false);
    }
  };

  /** 제안 경로 확정·거절 — AnswerQuestion 예약 키 재사용(새 라우트 없음). */
  const submitReserved = async (propertyKey) => {
    if (!flow || loading) return;
    setLoading(true);
    setError("");
    try {
      const apiResponse = await fetch("/api/onto/action/AnswerQuestion", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: flow.sessionId, property_key: propertyKey, option_value: "1",
        }),
      });
      if (!apiResponse.ok) throw new Error(`HTTP ${apiResponse.status}`);
      const nextResponse = await apiResponse.json();
      const next = { sessionId: flow.sessionId, response: nextResponse };
      setFlow(next);
      saveCached(q, candidateKey, next);
    } catch {
      setError("처리하지 못했습니다. 다시 시도해 주세요.");
    } finally {
      setLoading(false);
    }
  };

  const confirmTerminal = async () => {
    if (!flow || !terminalHsk || loading || flow.confirmedHsk === terminalHsk) return;
    setLoading(true);
    setError("");
    try {
      const apiResponse = await fetch("/api/onto/action/AcceptHSK", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: flow.sessionId, hsk: terminalHsk }),
      });
      if (!apiResponse.ok) throw new Error(`HTTP ${apiResponse.status}`);
      const next = { ...flow, confirmedHsk: terminalHsk };
      setFlow(next);
      saveCached(q, candidateKey, next);
    } catch {
      setError("최종 세번을 저장하지 못했습니다. 다시 시도해 주세요.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    sessionStorage.removeItem(storageKey(q, candidateKey));
    setFlow(null);
    setError("");
    setReloadToken((value) => value + 1);
  };

  return (
    <section style={panelStyle} aria-label="검색 후보 공식 분류">
      <button type="button" onClick={() => setOpen((value) => !value)} style={headerStyle}>
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <CircleHelp size={17} />
          <strong>검색 정확도 높이기</strong>
          {question && <span style={stageStyle}>{stageLabel(question, response)}</span>}
        </span>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      {open && (
        <div style={{ padding: "14px 16px 16px" }}>
          {error && <div role="alert" style={{ ...mutedStyle, color: "#1a1a1a", fontWeight: 700, marginBottom: 10 }}>{error}</div>}

          {tooBroad ? (
            <div role="status" style={{ ...terminalStyle, color: "#1a1a1a" }}>
              <div style={{ fontWeight: 750 }}>품명을 조금 더 적어 주세요.</div>
              <div style={{ ...mutedStyle, marginTop: 6 }}>
                「{q}」는 관세율표에서 직접 찾지 못했습니다. 지금 남은 후보는 이 말을 언급한
                다른 물품들이라, 어느 상품군인지 고르는 질문이 의미가 없습니다. 재질·용도·형태 중
                하나만 덧붙이면 바로 좁혀집니다.
              </div>
              <div style={{ ...mutedStyle, marginTop: 6 }}>
                예) 「말」 → 「살아있는 경주말」 · 「말고기」 · 「말용 샴푸」
              </div>
            </div>
          ) : loading && !response ? (
            <div style={mutedStyle}>검색 결과를 공식 HS 계층에 연결하고 있습니다.</div>
          ) : response && response.proposed_path ? (
            <div style={terminalStyle}>
              <div style={{ fontSize: 21, fontWeight: 800, fontFamily: "JetBrains Mono, monospace", color: "#1a1a1a" }}>
                {formatHsk(response.proposed_hsk)}
              </div>
              <div style={{ ...mutedStyle, marginTop: 5 }}>{describeProposalBasis(proposal)}</div>
              <div style={{ ...mutedStyle, marginTop: 3 }}>{response.proposed_note_ko}</div>
              <div style={{ display: "grid", gap: 4, marginTop: 9 }}>
                {(response.proposed_steps || []).map((step, index) => (
                  <div key={step.node_id} style={{ fontSize: 12 }}>
                    <span style={{ color: "#667085" }}>{index + 1}. {step.question_ko}</span>
                    {" → "}
                    <strong>{step.answer_label_ko}</strong>
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", gap: 7, marginTop: 12 }}>
                <button
                  type="button"
                  disabled={loading}
                  onClick={() => submitReserved("__accept_path__")}
                  style={{ ...optionStyle, borderColor: "#1a1a1a", background: "#1a1a1a", color: "#fff", fontWeight: 700 }}
                >
                  이 경로로 확정
                </button>
                <button
                  type="button"
                  disabled={loading}
                  onClick={() => submitReserved("__decline_path__")}
                  style={{ ...optionStyle, fontWeight: 600 }}
                >
                  직접 고르기
                </button>
              </div>
            </div>
          ) : response && response.official_frame_unavailable ? (
            <div role="alert" style={{ ...terminalStyle, color: "#1a1a1a" }}>
              <div style={{ fontWeight: 750 }}>선택한 호의 검증된 공식 분류 프레임을 불러오지 못했습니다.</div>
              <div style={{ ...mutedStyle, marginTop: 6 }}>
                임의의 세번으로 확정하지 않았습니다. 잠시 후 다시 시도해 주세요.
              </div>
            </div>
          ) : response && response.resolution_status === "unresolved" && !question ? (
            <div role="alert" style={{ ...terminalStyle, color: "#1a1a1a" }}>
              <div style={{ fontWeight: 750 }}>확정하지 않고 보류했습니다.</div>
              <div style={{ ...mutedStyle, marginTop: 6 }}>
                {response.unresolved_detail_ko
                  || "남은 후보를 구분할 근거가 없어 임의로 확정하지 않았습니다."}
              </div>
              {Array.isArray(response.missing_facts) && response.missing_facts.length > 0 && (
                <div style={{ marginTop: 9 }}>
                  <div style={{ fontSize: 12, fontWeight: 700 }}>확정에 필요한 자료</div>
                  {response.missing_facts.map((item) => (
                    <div key={item.fact_id} style={{ ...mutedStyle, marginTop: 5 }}>
                      · {item.question_ko}
                      <div style={{ paddingLeft: 10 }}>확인 위치: {item.observable_ko}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : terminalHsk ? (
            <div style={terminalStyle}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 750, color: "#1a1a1a" }}>
                <CheckCircle2 size={18} /> 공식 결정 경로가 완료되었습니다.
              </div>
              <div style={{ marginTop: 8, fontSize: 21, fontWeight: 800, fontFamily: "JetBrains Mono, monospace" }}>
                {formatHsk(terminalHsk)}
              </div>
              <div style={{ ...mutedStyle, marginTop: 5 }}>선택한 사실을 공식 부·류·호와 세번 분기 순서로 적용한 결과입니다.</div>
              <button
                type="button"
                disabled={loading || flow.confirmedHsk === terminalHsk}
                style={{ ...optionStyle, marginTop: 12, marginRight: 7, borderColor: "#1a1a1a", color: "#1a1a1a", fontWeight: 700 }}
                onClick={confirmTerminal}
              >
                {flow.confirmedHsk === terminalHsk ? "확정·저장 완료" : "이 세번으로 확정·저장"}
              </button>
              <button
                type="button"
                style={{ ...optionStyle, marginTop: 12, borderColor: "#1a1a1a", color: "#1a1a1a", fontWeight: 700 }}
                onClick={() => onRowClick && onRowClick(terminalHsk.slice(0, 4), terminalHsk)}
              >
                이 세번 상세 보기
              </button>
              <GoogleHsCrosscheck query={q} hsk={terminalHsk} />
            </div>
          ) : question ? (
            <>
              <div style={{ fontSize: 12, color: "#1a1a1a", fontWeight: 700, marginBottom: 8 }}>
                {stageLabel(question, response)}
              </div>
              <div style={{ fontSize: 15, fontWeight: 750, marginBottom: 5 }}>
                {question.question_ko || question.display_name_ko || "어느 분류에 해당하나요?"}
              </div>
              {(question.decision_basis_ko || question.reasoning) && (
                <div style={mutedStyle}>{question.decision_basis_ko || question.reasoning}</div>
              )}
              <div style={{ display: "grid", gap: 7, marginTop: 12 }}>
                {options.filter((option) => option.confidence !== "statistical").map((option) => {
                  const facts = optionFacts(option);
                  const selected = selectedOfficialOption?.value === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      disabled={loading || option.is_reachable === false}
                      onClick={() => {
                        // "Cannot tell" / "not applicable" carry no evidence, so they submit
                        // immediately instead of opening a form the user cannot fill.
                        if (!optionRequiresEvidence(question, option)) {
                          answer(option);
                          return;
                        }
                        if (!selected) setOfficialEvidence(emptyOfficialEvidence());
                        setSelectedOfficialOption(option);
                      }}
                      style={{
                        ...answerStyle,
                        border: selected ? "1px solid #1a1a1a" : answerStyle.border,
                        background: selected ? "#f1f1f1" : answerStyle.background,
                        opacity: loading || option.is_reachable === false ? 0.55 : 1,
                      }}
                    >
                      {(() => {
                        // 호 선택지는 열거 표제를 그대로 쓰면 검색어와 무관한 형제가 앞에 온다.
                        // 검색어 항목을 앞으로 끌고, 서버가 자른 한정 문구를 부제로 되살린다.
                        const raw = option.label_ko || option.label || option.value;
                        if (question.property_key !== "__heading__") {
                          return <span style={{ fontWeight: 700 }}>{raw}</span>;
                        }
                        const { lead, code, condensed } = condenseHeadingLabel(raw, q);
                        const qualifier = headingQualifier(
                          data && data.tariff && data.tariff.headingMatches, code || option.value,
                        );
                        return (
                          <>
                            <span style={{ fontWeight: 700 }}>
                              {lead}{code ? ` · ${code}` : ""}
                            </span>
                            {qualifier && (
                              <span style={factLineStyle}>{qualifier}</span>
                            )}
                            {condensed && (
                              <span style={{ ...factLineStyle, color: "#94a3b8" }} title={raw}>
                                호 표제: {raw}
                              </span>
                            )}
                          </>
                        );
                      })()}
                      {facts.required.length > 0 && (
                        <span style={factLineStyle}>필수: {facts.required.join(" · ")}</span>
                      )}
                      {facts.excluded.length > 0 && (
                        <span style={{ ...factLineStyle, color: "#4b4b4b" }}>제외: {facts.excluded.join(" · ")}</span>
                      )}
                    </button>
                  );
                })}
              </div>
              {isOfficialQuestion && selectedOfficialOption && (
                <div style={{ marginTop: 10 }}>
                  <OfficialFrameEvidenceForm
                    value={officialEvidence}
                    onChange={setOfficialEvidence}
                    disabled={loading}
                    requiredFacts={selectedOfficialOption.required_facts || []}
                    admissibleSources={admissibleSourcesOf(selectedOfficialOption)}
                    observableKo={question.observable_ko || ""}
                  />
                  <button
                    type="button"
                    disabled={loading || !evidenceReady}
                    onClick={() => answer(selectedOfficialOption)}
                    style={{
                      ...optionStyle,
                      marginTop: 9,
                      borderColor: "#1a1a1a",
                      color: "#1a1a1a",
                      fontWeight: 700,
                      opacity: evidenceReady ? 1 : 0.55,
                    }}
                  >
                    확인한 근거로 이 분기 제출
                  </button>
                </div>
              )}
              {loading && <div aria-live="polite" style={{ ...mutedStyle, marginTop: 9 }}>선택한 경로의 다음 공식 분기를 확인하고 있습니다.</div>}
              <div style={{ ...mutedStyle, marginTop: 10 }}>
                먼저 검색 후보의 부·류·호를 좁힌 뒤, 한 호가 남으면 검증된 공식 세번 결정트리로 자동 전환됩니다.
              </div>
            </>
          ) : (
            <div style={mutedStyle}>현재 검색 후보에서 이어갈 공식 분류 질문을 찾지 못했습니다.</div>
          )}

          {response && Array.isArray(response.sibling_boundaries)
            && response.sibling_boundaries.length > 0 && (
            <div style={{ marginTop: 12, padding: "10px 12px", border: "1px solid #c9c9c7", borderRadius: 8, background: "#f7f7f6" }}>
              <div style={{ fontSize: 12.5, fontWeight: 750, color: "#1a1a1a" }}>
                재질·조직이 다르면 다른 호입니다
              </div>
              <div style={{ ...mutedStyle, marginTop: 4 }}>
                아래는 큐레이션된 경계 정의가 지목한 형제 호입니다. 해당하면 그 호로 다시 검색하세요.
              </div>
              {response.sibling_boundaries.map((b) => (
                <div key={b.boundary_id} style={{ marginTop: 7, fontSize: 12, color: "#4b4b4b" }}>
                  <strong>{b.target_hs4}</strong>
                  {b.predicate ? ` — ${b.predicate}` : ""}
                  {b.legal_source && (
                    <div style={factLineStyle}>📖 {b.legal_source}</div>
                  )}
                </div>
              ))}
            </div>
          )}

          {(flow || error) && (
            <div style={{ marginTop: 13, paddingTop: 10, borderTop: "1px solid var(--audit-border)" }}>
              <button type="button" onClick={reset} style={resetStyle}>
                <RotateCcw size={13} /> 처음부터 다시 분류
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

const panelStyle = { border: "1px solid #d0d5dd", borderRadius: 8, background: "#fcfcfb", marginBottom: 12, overflow: "hidden" };
const headerStyle = { width: "100%", border: 0, background: "#f6f7f5", padding: "10px 14px", display: "flex", alignItems: "center", justifyContent: "space-between", color: "var(--audit-text)", cursor: "pointer", fontFamily: "inherit" };
const stageStyle = { fontSize: 11, fontWeight: 650, color: "#1a1a1a", border: "1px solid #c9c9c7", background: "#f1f1f1", borderRadius: 999, padding: "1px 7px" };
const mutedStyle = { fontSize: 12, color: "var(--audit-text-muted)", lineHeight: 1.55 };
const optionStyle = { border: "1px solid #b8c1cc", borderRadius: 7, background: "#fff", padding: "8px 11px", cursor: "pointer", fontFamily: "inherit", color: "var(--audit-text)" };
const answerStyle = { ...optionStyle, width: "100%", display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4, textAlign: "left" };
const factLineStyle = { fontSize: 11.5, color: "#667085", lineHeight: 1.45 };
const terminalStyle = { border: "1px solid #c9c9c7", borderRadius: 8, background: "#f7f7f6", padding: 14 };
const resetStyle = { border: 0, background: "transparent", color: "#667085", display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer", fontFamily: "inherit", fontSize: 11.5 };
